import moment from "moment";

export class DateHelper {
  static getDayAgo(date: Date, subtractDays: number) {
    return new Date(date.getTime() - subtractDays * 24 * 60 * 60 * 1000);
  }

  static addDays = function (date: Date,days) {
    days = parseInt(days, 10);
    return new Date(date.getTime()  + 1000 * 60 * 60 * 24 * days);
  }
}

/**
 * FRO-10891: date format now follows the browser locale (was the app UI language),
 * so date pickers stay consistent with the rest of the app.
 */
export const getFormatDateByLocal = () => getBrowserDateFormat();

/**
 * FRO-10891: Resolve the user's BROWSER locale (e.g. "en-GB", "nl-BE", "fr-BE").
 * Used to localize the numeric date notation independently of the app UI language.
 */
export const getBrowserLocale = (): string => {
  if (typeof navigator === 'undefined') return 'en-GB';
  return (navigator.languages && navigator.languages[0]) || navigator.language || 'en-GB';
};

/**
 * FRO-10891: Map the browser locale to a moment locale that is actually loaded,
 * so we never silently fall back to the American `en` default. The required
 * locales are imported in main.ts.
 */
const resolveMomentLocale = (locale: string): string => {
  const lower = locale.toLowerCase();
  const loaded = moment.locales();
  if (loaded.indexOf(lower) !== -1) return lower;
  const parts = lower.split('-');
  const base = parts[0];
  // English is day-first everywhere except the US region; avoid moment's American `en`.
  if (base === 'en') return parts[1] === 'us' ? 'en' : 'en-gb';
  if (loaded.indexOf(base) !== -1) return base;
  return 'en-gb';
};

/**
 * FRO-10891: Numeric moment date format for the browser locale, taken from
 * moment's own locale data, e.g. "DD/MM/YYYY" or "MM/DD/YYYY". Separators are
 * normalized to "/" so the notation looks the same regardless of locale.
 */
export const getBrowserDateFormat = (): string =>
  moment.localeData(resolveMomentLocale(getBrowserLocale())).longDateFormat('L').replace(/[-.]/g, '/');

/**
 * FRO-10891: Short time format for the browser locale (e.g. "HH:mm" or "h:mm A"),
 * taken from moment's locale data.
 */
export const getBrowserTimeFormat = (): string =>
  moment.localeData(resolveMomentLocale(getBrowserLocale())).longDateFormat('LT');

/**
 * FRO-11282: Localized date + space + localized time — the app's standard
 * datetime notation (the same join the grid's datetime cells render).
 */
export const getBrowserDateTimeFormat = (): string =>
  `${getBrowserDateFormat()} ${getBrowserTimeFormat()}`;

const isDayBeforeMonth = (): boolean => {
  const format = getBrowserDateFormat();
  return format.indexOf('D') < format.indexOf('M');
};

/**
 * FRO-10891: Month + day (no year) following the browser locale's order, with the
 * month abbreviated, e.g. "DD MMM" or "MMM DD".
 */
export const getBrowserMonthDayFormat = (): string => (isDayBeforeMonth() ? 'DD MMM' : 'MMM DD');

/**
 * FRO-10891: Day + month numeric (no year) following the browser locale's order,
 * e.g. "DD/MM" or "MM/DD".
 */
export const getBrowserDayMonthFormat = (): string => (isDayBeforeMonth() ? 'DD/MM' : 'MM/DD');

/**
 * SWA-7497: Day + full month NAME (no year) — e.g. "29 augustus" / "August 29".
 *
 * Month and weekday names are words the user reads, so they follow the selected
 * app language (main.ts sets moment's locale from `localStorage.lang`), not the
 * browser locale that FRO-10891 uses for numeric notation. The order therefore
 * comes from that same active locale rather than from `isDayBeforeMonth()`.
 */
export const getAppFullMonthDayFormat = (): string => {
  const numeric = moment.localeData().longDateFormat('L');
  return numeric.indexOf('D') < numeric.indexOf('M') ? 'D MMMM' : 'MMMM D';
};
